import React, { useRef, useLayoutEffect, useState, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { useSpring, useTransition, animated } from 'react-spring';
import classNames from 'classnames';
import useOpenStatus from './hooks/use-open-status';
import AccordionContext from './context/accordion-context';

function AccordionContent({
  component: Component = 'div',
  shouldRenderCollapsed = false,
  itemId,
  children,
  contentWrapperClassName = '',
  ...otherProps
}) {
  const [contentHeight, setContentHeight] = useState(0);
  const isOpen = useOpenStatus(itemId);
  const { shouldPreventAnimation } = useContext(AccordionContext);
  const contentRef = useRef(null);

  const resetContentHeight = useCallback(() => {
    setContentHeight((height) => (contentRef.current && contentRef.current.scrollHeight) || height);
  }, [contentRef, setContentHeight]);

  useLayoutEffect(() => {
    resetContentHeight();

    window.addEventListener('resize', resetContentHeight);
    return () => window.removeEventListener('resize', resetContentHeight);
  });

  const contentStyles = useSpring({
    maxHeight: isOpen ? `${contentHeight}px` : '0px',
    overflow: 'hidden',
  });

  const transitions = useTransition(isOpen, null, {
    from: { maxHeight: '0px', overflow: 'hidden' },
    enter: { maxHeight: `${contentHeight}px` },
    update: { maxHeight: `${contentHeight}px` },
    leave: { maxHeight: '0px' },
  });

  const contentWrapperClassNames = classNames({
    [contentWrapperClassName]: contentWrapperClassName,
    [`${contentWrapperClassName}--closed`]: contentWrapperClassName && !isOpen,
  });

  if (shouldPreventAnimation) {
    return (
      <React.Fragment>
        {isOpen && (
          <div className={contentWrapperClassNames}>
            <Component {...otherProps} ref={contentRef}>
              {children}
            </Component>
          </div>
        )}
        {!isOpen && shouldRenderCollapsed && (
          <div
            style={{ maxHeight: '0px', overflow: 'hidden' }}
            className={contentWrapperClassNames}
          >
            <Component {...otherProps} ref={contentRef}>
              {children}
            </Component>
          </div>
        )}
      </React.Fragment>
    );
  }

  if (shouldRenderCollapsed) {
    return (
      <animated.div style={contentStyles} className={contentWrapperClassNames}>
        <Component {...otherProps} ref={contentRef}>
          {children}
        </Component>
      </animated.div>
    );
  }

  return transitions.map(
    ({ item, key, props: styles }) =>
      item && (
        <animated.div key={key} style={styles} className={contentWrapperClassNames}>
          <Component {...otherProps} ref={contentRef}>
            {children}
          </Component>
        </animated.div>
      )
  );
}

AccordionContent.propTypes = {
  component: PropTypes.elementType,
  itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  children: PropTypes.node.isRequired,
  shouldRenderCollapsed: PropTypes.bool,
  contentWrapperClassName: PropTypes.string,
};

export default AccordionContent;
